// Rug Room Shots: the "Rug detail photo" block of the single Regenerate (Review dialog and compare view),
// build spec 4.3. The rug's close-up (edge, binding, pile) can go to Gemini as IMAGE 3: either the detail photo
// on the image server (GET targets/{id}/detail-photo "default": own size, else another size of the same outline)
// or a photo uploaded here (POST targets/{id}/detail-photo; the last upload for this rug's outline - round, square or
// any other shape - is offered again, with the ItemID it was uploaded for).
// On by default only when the row was rejected as "Rug binding" and a default exists. Hidden while the worker
// does not support it (config.features.detailPhotos false).
//
// Controlled: value = { on, source: 'default' | 'upload', defaultKey, uploadKey }. detailBody(value) gives the
// regenerate body fields; detailReady(value) says whether the choice can be sent.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Checkbox, FormControlLabel, RadioGroup, Radio, Button, Alert, CircularProgress, Stack, Chip,
} from '@mui/material';
import { MdUpload } from 'react-icons/md';
import rrsApi from './api';
import { Tile, ImageLightbox, NyTime, serverText } from './common';

export const DETAIL_OFF = { on: false, source: 'default', defaultKey: null, uploadKey: null };

export const detailReady = (v) => !v || !v.on
  || (v.source === 'default' && !!v.defaultKey)
  || (v.source === 'upload' && !!v.uploadKey);

export const detailBody = (v) => {
  if (!v || !v.on) return { detailMode: 'none' };
  if (v.source === 'upload') return { detailMode: 'upload', detailUploadKey: v.uploadKey };
  return { detailMode: 'default' };
};

const KIND_WORDS = { own: 'same size', other_size: 'another size' };
// What a generation sent as IMAGE 3 (rrs_generations.detail_source).
export const DETAIL_SOURCE_WORDS = {
  own: 'the detail photo of this size on the image server',
  other_size: 'the detail photo of another size on the image server',
  upload: 'a photo uploaded on this page',
};
const ACCEPT = 'image/jpeg,image/png,image/webp';
export const NOT_READY_TEXT = 'The worker has not been updated for this yet.';

const DetailPhotoBlock = ({
  targetId, reviewReasonCode, config, value, onChange, disabled, notify, dense,
}) => {
  const features = (config && config.features) || {};
  const enabled = features.detailPhotos === true;
  const maxBytes = Number(config && config.maxRoomUploadBytes) || 41943040;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const defaultApplied = useRef(false);
  const inputRef = useRef(null);
  const v = value || DETAIL_OFF;

  const load = useCallback(async () => {
    if (!enabled || !targetId) return;
    setLoading(true);
    setError('');
    try {
      const res = await rrsApi.targetDetail(targetId);
      setInfo(res || {});
    } catch (e) {
      setError(serverText(e));
      setInfo({});
    } finally {
      setLoading(false);
    }
  }, [enabled, targetId]);

  useEffect(() => {
    defaultApplied.current = false;
    setInfo(null);
    setUploaded(null);
    load();
  }, [load]);

  const def = info && info.default && info.default.imageKey ? info.default : null;
  const lastUpload = uploaded || (info && info.lastUpload && info.lastUpload.imageKey ? info.lastUpload : null);

  // On by default only for a "Rug binding" rejection with a default photo (applied once per load).
  useEffect(() => {
    if (!info || defaultApplied.current) return;
    defaultApplied.current = true;
    if (reviewReasonCode === 'rug_binding' && def) {
      onChange({ on: true, source: 'default', defaultKey: def.imageKey, uploadKey: null });
    }
  }, [info, def, reviewReasonCode, onChange]);

  const setOn = (on) => {
    if (!on) { onChange({ ...v, on: false }); return; }
    const source = def ? 'default' : 'upload';
    onChange({
      on: true, source, defaultKey: def ? def.imageKey : null, uploadKey: lastUpload ? lastUpload.imageKey : null,
    });
  };

  const setSource = (source) => onChange({
    ...v, on: true, source, defaultKey: def ? def.imageKey : null, uploadKey: lastUpload ? lastUpload.imageKey : null,
  });

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > maxBytes) { setError(`The photo is larger than ${Math.round(maxBytes / 1048576)} MB.`); return; }
    setUploading(true);
    setError('');
    try {
      const res = await rrsApi.uploadDetail(targetId, f);
      const up = {
        imageKey: res.imageKey, smallUrl: res.smallUrl, largeUrl: res.largeUrl, w: res.w, h: res.h, justNow: true,
      };
      setUploaded(up);
      onChange({ on: true, source: 'upload', defaultKey: def ? def.imageKey : null, uploadKey: up.imageKey });
      ((res && res.warnings) || []).forEach((w) => { if (w && w.message && notify) notify(w.message, 'warning'); });
    } catch (err) {
      setError(err && err.status === 413 ? `The photo is larger than ${Math.round(maxBytes / 1048576)} MB.` : serverText(err));
    } finally {
      setUploading(false);
    }
  };

  const title = (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: dense ? 1.5 : 2 }}>Rug detail photo</Typography>
  );

  if (!enabled) {
    return (
      <Box>
        {title}
        <Typography variant="caption" color="text.secondary" display="block">{NOT_READY_TEXT}</Typography>
      </Box>
    );
  }

  const thumb = dense ? 64 : 84;
  const nothing = !!info && !def && !lastUpload;

  return (
    <Box>
      {title}
      <FormControlLabel
        control={<Checkbox size="small" checked={!!v.on} onChange={(e) => setOn(e.target.checked)} disabled={disabled || loading} />}
        label={<Typography variant="body2">Also send a detail photo of the rug</Typography>}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        Helps copy the rug&apos;s real edge, binding and pile. Costs about the same.
      </Typography>
      {loading && <CircularProgress size={18} />}
      {error && <Alert severity="warning" sx={{ my: 0.75 }}>{error}</Alert>}
      {nothing && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 0.75 }}>
          This rug has no detail photo on the image server yet. Upload one to use this.
        </Typography>
      )}
      {v.on && info && (
        <RadioGroup value={v.source} onChange={(e) => setSource(e.target.value)} sx={{ pl: 1 }}>
          <FormControlLabel
            value="default" disabled={disabled || !def}
            control={<Radio size="small" />}
            label={<Typography variant="body2">Detail photo on the image server{def ? '' : ' (none yet)'}</Typography>}
          />
          {def && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 4, mb: 0.75 }}>
              <Tile
                src={def.smallUrl} alt={def.imageKey} size={thumb}
                onClick={def.largeUrl || def.originalUrl || def.smallUrl ? () => setLightbox({
                  title: def.imageKey, src: def.largeUrl || def.originalUrl || def.smallUrl, downloadUrl: def.originalUrl,
                }) : undefined}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', wordBreak: 'break-all' }}>{def.imageKey}</Typography>
                {def.kind && (
                  <Chip
                    size="small" variant="outlined" color={def.kind === 'own' ? 'success' : 'warning'}
                    label={KIND_WORDS[def.kind] || String(def.kind).replace(/_/g, ' ')} sx={{ height: 18, fontSize: '0.65rem', mt: 0.25 }}
                  />
                )}
              </Box>
            </Stack>
          )}
          <FormControlLabel
            value="upload" disabled={disabled}
            control={<Radio size="small" />}
            label={<Typography variant="body2">A photo I upload</Typography>}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 4 }}>
            <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
            <Button
              size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <MdUpload />}
              onClick={() => inputRef.current && inputRef.current.click()} disabled={disabled || uploading}
            >
              {uploading ? 'Uploading…' : (lastUpload ? 'Upload another' : 'Upload…')}
            </Button>
            {lastUpload && (
              <>
                <Tile
                  src={lastUpload.smallUrl} alt="uploaded detail photo" size={thumb}
                  onClick={lastUpload.largeUrl || lastUpload.smallUrl ? () => setLightbox({
                    title: 'Uploaded detail photo', src: lastUpload.largeUrl || lastUpload.smallUrl,
                  }) : undefined}
                />
                <Typography variant="caption" color="text.secondary">
                  {lastUpload.justNow ? 'Uploaded just now' : `Last upload for this rug${lastUpload.uploadedForItemId ? ` (uploaded for ${lastUpload.uploadedForItemId})` : ''}`}
                  {lastUpload.w && lastUpload.h ? ` · ${lastUpload.w} x ${lastUpload.h}` : ''}
                  {!lastUpload.justNow && lastUpload.uploadedByEmail ? ` · ${lastUpload.uploadedByEmail}` : ''}
                  {!lastUpload.justNow && lastUpload.uploadedAt ? <> · <NyTime ts={lastUpload.uploadedAt} variant="caption" /></> : null}
                </Typography>
              </>
            )}
          </Stack>
          {v.source === 'upload' && !lastUpload && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: 0.5 }}>
              JPG, PNG or WebP, up to {Math.round(maxBytes / 1048576)} MB; the short side must be at least 512 px.
            </Typography>
          )}
        </RadioGroup>
      )}
      <ImageLightbox
        open={!!lightbox} onClose={() => setLightbox(null)}
        items={lightbox ? [{ key: 'detail', label: 'Detail photo', src: lightbox.src, downloadUrl: lightbox.downloadUrl, downloadLabel: 'Open original' }] : []}
        initialKey="detail" title={lightbox ? lightbox.title : ''}
      />
    </Box>
  );
};

export default DetailPhotoBlock;
